import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import type { GoogleAdsAccount } from '@/hooks/useGoogleAdsConnection';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: GoogleAdsAccount[];
  currentCustomerId: string | null;
  onSelect: (customerId: string) => Promise<unknown>;
  isSaving: boolean;
}

export function GoogleAdsAccountPickerDialog({
  open,
  onOpenChange,
  accounts,
  currentCustomerId,
  onSelect,
  isSaving,
}: Props) {
  const [picked, setPicked] = useState<string | null>(currentCustomerId);

  const leafAccounts = accounts.filter((a) => !a.is_manager);

  const handleSave = async () => {
    if (!picked) {
      toast({ title: 'Pick an account first', variant: 'destructive' });
      return;
    }
    try {
      await onSelect(picked);
      toast({ title: 'Active Google Ads account updated' });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Failed to set active account',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose your Google Ads account</DialogTitle>
          <DialogDescription>
            Your Google login can access multiple Google Ads accounts. Pick the one you want
            this company to report on. You can switch later from this page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {leafAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No selectable accounts found (manager accounts cannot be reported on directly).
              Reconnect with a Google login that has access to a leaf Google Ads account.
            </p>
          ) : (
            leafAccounts.map((acc) => {
              const isPicked = picked === acc.customer_id;
              const isCurrent = currentCustomerId === acc.customer_id;
              return (
                <button
                  key={acc.customer_id}
                  type="button"
                  onClick={() => setPicked(acc.customer_id)}
                  className={`w-full text-left rounded-lg border p-3 transition ${
                    isPicked
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {acc.descriptive_name || 'Unnamed account'}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        ID {acc.customer_id}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {acc.timezone ?? 'Unknown TZ'} · {acc.currency_code ?? 'Unknown currency'}
                      </div>
                    </div>
                    {isCurrent && <Badge variant="secondary">Current</Badge>}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !picked || picked === currentCustomerId || leafAccounts.length === 0}
          >
            {isSaving ? 'Saving...' : 'Use this account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
